/**
 * P5 offline save queue — the Preferences adapter + drain driver.
 *
 * The thin I/O layer around the pure reducers in saveQueue.ts. Persists the
 * queue in Capacitor Preferences (native UserDefaults → survives app kill;
 * web localStorage fallback via the plugin). Every mutation re-reads the
 * latest queue before writing so a save that fails DURING a drain isn't
 * clobbered (single-user, but drains and enqueues interleave across awaits).
 *
 * ⛔ B56 — THE STORE IS DEVICE-GLOBAL, THE ENTRIES ARE NOT. `STORE_KEY` is one
 * blob for the whole device, so every function here that reads, POSTs, mutates
 * or deletes an entry takes the current account's id and applies the ownership
 * rule in saveQueue.ts (`ownershipOf`). Another account's queued run is the
 * only copy of a real run: it is never POSTed under this session's token, never
 * shown to this session, and never deleted by this session. It drains when its
 * owner signs back in. See the B56 header in saveQueue.ts for the full rule and
 * the legacy (unattributed) ruling.
 */

import { Preferences } from '@capacitor/preferences';
import type { AuthFetchFn } from '../../hooks/useAuthFetch';
import {
  type QueuedRun,
  type RunSavePayload,
  drainableForUser,
  enqueue,
  ownedByUser,
  ownershipOf,
  rearm,
  recordFailure,
  removeByKey,
} from './saveQueue';

const STORE_KEY = 'tayro.runSaveQueue.v1';

/**
 * The whole device-global blob, every owner's entries included.
 *
 * ⛔ B56 — INTERNAL. This is the read the mutators build on and the read the
 * store's own tests assert against; it is NOT a surface reader. Anything that
 * displays, POSTs or deletes an entry must go through `loadQueue(viewerId)` or
 * one of the owner-scoped mutators, or it reopens B56.
 */
export async function loadRawQueue(): Promise<QueuedRun[]> {
  try {
    const { value } = await Preferences.get({ key: STORE_KEY });
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as QueuedRun[]) : [];
  } catch {
    return [];
  }
}

/**
 * The queued runs THIS account recorded — the surface reader (B56).
 *
 * Foreign and unattributed entries are absent from the result, not merely
 * unclickable: a queued row carries the run's date, distance, duration, pace
 * and elevation, so rendering someone else's is already an exposure even
 * before anything is POSTed. A signed-out viewer sees nothing.
 */
export async function loadQueue(viewerId: string | null | undefined): Promise<QueuedRun[]> {
  return ownedByUser(await loadRawQueue(), viewerId);
}

async function writeQueue(queue: QueuedRun[]): Promise<void> {
  try {
    await Preferences.set({ key: STORE_KEY, value: JSON.stringify(queue) });
  } catch {
    /* best-effort — a persist failure just means we retry from the last
       persisted state; the run is never lost from an in-flight failure. */
  }
  notify();
}

/** Read-modify-write keyed by the pure transform. SERIALIZED through a
 *  promise chain: load→transform→write spans awaits, so two concurrent
 *  mutations (a drain-pass recordFailure interleaving with an enqueue of a
 *  fresh failed save) could otherwise clobber each other from stale reads —
 *  losing the just-queued run, the exact loss this queue exists to prevent
 *  (P5 review, 2026-07-09). The chain makes every mutation read the
 *  previous one's write. */
let mutateChain: Promise<void> = Promise.resolve();
function mutate(fn: (q: QueuedRun[]) => QueuedRun[]): Promise<void> {
  const next = mutateChain.then(async () => {
    // Reads the FULL device blob on purpose: the transform must write back
    // every owner's entries, not just this session's (B56 — a mutation that
    // round-tripped only the viewer's own runs would delete everyone else's).
    await writeQueue(fn(await loadRawQueue()));
  });
  // Keep the chain alive even if a link rejects (writeQueue/loadQueue are
  // no-throw today; this is the belt for future edits).
  mutateChain = next.catch(() => {});
  return next;
}

// ── change signal (so the runs list reflects the queue live) ──────────
type Listener = () => void;
const listeners = new Set<Listener>();
export function onQueueChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function notify(): void {
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* a listener throwing must not break persistence */
    }
  }
}

/** Queue a save that just failed transiently (network / 5xx), stamped with the
 *  account that recorded it (B56 — `userId` is the claim that decides who may
 *  ever POST it). */
export async function enqueueFailedSave(
  payload: RunSavePayload,
  nowMs: number,
  userId: string | null | undefined,
): Promise<void> {
  await mutate((q) => enqueue(q, payload, nowMs, userId));
}

/** Drop a queued run (athlete discarded it). Owner-scoped: only the account
 *  that recorded it can discard it. */
export async function removeQueuedRun(
  clientKey: string,
  viewerId: string | null | undefined,
): Promise<void> {
  await mutate((q) => removeByKey(q, clientKey, viewerId));
}

/**
 * B190 fix4 — deletion residue. Drops every entry THIS account owns from the
 * device-global queue; every other account's entries (foreign or
 * unattributed) are left exactly as they are — the same B56 ownership rule
 * every other mutator here applies, so a deletion on this device can never
 * take a stranger's still-unsynced run with it, and never wipes the store
 * when it still holds someone else's entries.
 */
export async function removeQueuedRunsForUser(userId: string): Promise<void> {
  await mutate((q) => q.filter((entry) => ownershipOf(entry, userId) !== 'own'));
}

let draining = false;

/**
 * Drain the queue: POST each pending item to /api/runs. Success → drop it
 * (the client_key makes a re-POST of an already-saved run return the same
 * row, so a lost success is self-healing). Transient failure → record +
 * retry later. 4xx → needs_attention (surfaced, no auto-retry). Single-
 * flighted; stops the pass on a network failure (no point hammering a dead
 * connection — the next foreground/online event re-drains).
 *
 * ⛔ B56 — `viewerId` MUST be the account `fetchAuthed` is currently bound to.
 * Only that account's entries are POSTed; another account's queued run is left
 * exactly as it is, to drain the next time its own owner is signed in. A drain
 * with no signed-in account is a no-op: with nobody to attribute a POST to,
 * there is nothing this pass is allowed to send.
 *
 * B234 fix2 (security review, 2026-09-26) — a switch MID-DRAIN is the same
 * class as B56, just later: `expectedUid: viewerId` makes `fetchAuthed`
 * (`authFetch`'s own guard) refuse to send — no network call, a 409 with no
 * body — the moment the CURRENT session no longer matches the account this
 * pass started draining for. That 409 STOPS THE PASS; it is never
 * `recordFailure`d, because it is not this run's failure to record — the run
 * stayed exactly as it was, and it drains correctly the next time ITS owner
 * is signed in and foregrounds/comes online. Counting it against
 * `MAX_AUTO_ATTEMPTS` would eventually mark a perfectly good, unsent run
 * `needs_attention` for a reason that has nothing to do with the run itself.
 */
export async function drainQueue(
  fetchAuthed: AuthFetchFn,
  viewerId: string | null | undefined,
): Promise<void> {
  if (!viewerId) return;
  if (draining) return;
  draining = true;
  try {
    const snapshot = await loadRawQueue();
    for (const item of drainableForUser(snapshot, viewerId)) {
      const { clientKey } = item.payload;
      let status: number | null = null;
      let errorMsg: string | null = null;
      try {
        const res = await fetchAuthed('/api/runs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.payload),
          expectedUid: viewerId,
        });
        // B234 fix2 — the guard's refusal (`authFetch.ts`, no network call)
        // is a bare 409 with no body; `/api/runs` itself has no legitimate
        // 409 of its own. Stop this pass, untouched, rather than recording a
        // failure against an item the guard never actually sent.
        if (res.status === 409) break;
        if (res.ok) {
          await mutate((q) => removeByKey(q, clientKey, viewerId));
          continue;
        }
        status = res.status;
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        errorMsg = body?.error ?? `Save failed (${res.status}).`;
      } catch {
        status = null; // network error
        errorMsg = 'No connection.';
      }
      await mutate((q) => recordFailure(q, clientKey, viewerId, status, errorMsg));
      if (status === null) break; // dead connection — stop the pass
    }
  } finally {
    draining = false;
  }
}

/** Manual retry from the runs list: re-arm a needs_attention item, then drain.
 *  Owner-scoped end to end — a tap can only ever re-arm and send the tapper's
 *  own run (B56). */
export async function retryQueuedRun(
  fetchAuthed: AuthFetchFn,
  clientKey: string,
  viewerId: string | null | undefined,
): Promise<void> {
  await mutate((q) => rearm(q, clientKey, viewerId));
  await drainQueue(fetchAuthed, viewerId);
}
